import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DictationInput from '../../components/DictationInput';
import { useNavigate } from 'react-router-dom';
import {
  Users, ClipboardList, CalendarDays, Flag, AlertTriangle, MessageSquare,
  TrendingUp, Mic, User, Bell, Check, X, Target, Route, Home, Bed, MapPin,
  FileText, ChevronDown, ChevronUp, DollarSign, Eye, Gavel, Shield,
  ChevronRight, Loader2, Clock, ArrowLeft, Edit, Save, Calendar,
  Activity, CheckCircle2, XCircle, Heart, Coffee, Briefcase
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PeerDashboard() {
  const { user, token } = useAuth();
  const [clients, setClients] = useState([]);
  const [notes, setNotes] = useState([]);
  const [peerDocs, setPeerDocs] = useState([]);
  const [availability, setAvailability] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [flags, setFlags] = useState([]);
  const [goalRequests, setGoalRequests] = useState([]);
  const [roadmapApprovals, setRoadmapApprovals] = useState([]);
  const [houses, setHouses] = useState([]);
  const [services, setServices] = useState([]);
  const [courtCases, setCourt] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [expandedApproval, setExpandedApproval] = useState(null);
  const [expandedService, setExpandedService] = useState(null);
  const [expandedCase, setExpandedCase] = useState(null);
  const [expandedStat, setExpandedStat] = useState(null);
  const [expandedHouse, setExpandedHouse] = useState(null);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [expandedAppt, setExpandedAppt] = useState(null);
  const [drilldownClient, setDrilldownClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editServiceForm, setEditServiceForm] = useState({});
  const [reviewForm, setReviewForm] = useState({ feedback: '', progress: 0 });
  const [scorecard, setScorecard] = useState(null);
  const [weeklyDigest, setWeeklyDigest] = useState(null);
  const [expandedDigestClient, setExpandedDigestClient] = useState(null);
  const navigate = useNavigate();
  const f = useCallback((url) => fetch(`${API}/api${url}`, { credentials: 'include' }).then(r => r.json()).catch(() => []), []);

  useEffect(() => {
    f('/users?role=client').then(setClients);
    f('/session-notes').then(setNotes);
    f('/peer-docs').then(setPeerDocs);
    f('/appointments').then(setAppointments);
    f('/flags').then(setFlags);
    f('/goals/progress-requests?status=pending').then(setGoalRequests);
    f('/roadmap/pending-approvals').then(setRoadmapApprovals);
    f('/housing/houses').then(d => setHouses(Array.isArray(d) ? d : []));
    f('/services').then(d => setServices(Array.isArray(d) ? d : []));
    f('/court/cases').then(d => setCourt(Array.isArray(d?.cases) ? d.cases : Array.isArray(d) ? d : []));
    f('/checkins/recent').then(d => setCheckins(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/api/availability/me`, { headers }).then(r => r.json()).then(d => setAvailability(d.available)).catch(() => {});
    fetch(`${API}/api/digest/peer-scorecard`, { headers }).then(r => r.json()).then(d => setScorecard(d)).catch(() => {});
    fetch(`${API}/api/digest/peer-specialist`, { headers }).then(r => r.json()).then(d => setWeeklyDigest(d)).catch(() => {});
  }, []);

  const assignedClients = clients.filter(c => (user.assigned_clients || []).includes(c.id));
  const activeFlags = flags.filter(fl => !fl.resolved);
  const todayAppts = appointments.filter(a => a.status === 'scheduled').slice(0, 10);
  const hasBio = user.profile?.bio;
  const totalPending = goalRequests.length + roadmapApprovals.length;
  const clientMap = {};
  clients.forEach(c => { clientMap[c.id] = c; });

  const handleAvailabilityToggle = async (value) => {
    setAvailability(value);
    setAvailabilityLoading(true);
    try {
      await fetch(`${API}/api/availability/me`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ available: value }) });
      toast.success(value ? 'You are marked available for emergency support' : 'You are now unavailable');
    } catch { toast.error('Unable to update availability'); setAvailability(!value); }
    finally { setAvailabilityLoading(false); }
  };

  const handleGoalReview = async (requestId, action) => {
    try {
      await fetch(`${API}/api/goals/progress-requests/${requestId}`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, progress: reviewForm.progress || null, feedback: reviewForm.feedback })
      });
      toast.success(`Goal progress ${action}d`);
      setExpandedApproval(null);
      setReviewForm({ feedback: '', progress: 0 });
      f('/goals/progress-requests?status=pending').then(setGoalRequests);
    } catch { toast.error('Failed to review'); }
  };

  const handleRoadmapReview = async (milestoneId, action, comments = '') => {
    try {
      await fetch(`${API}/api/roadmap/approve/${milestoneId}`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, comments })
      });
      toast.success(`Milestone ${action}d`);
      f('/roadmap/pending-approvals').then(setRoadmapApprovals);
    } catch { toast.error('Failed to review'); }
  };

  // Load full client detail for drill-down
  const loadClientDetail = async (clientId) => {
    setDrilldownClient(clientId);
    try {
      const [userData, goalsData, checkinsData, flagsData, notesData] = await Promise.all([
        f(`/users/${clientId}`),
        f(`/goals?user_id=${clientId}`),
        f(`/checkins?user_id=${clientId}`),
        f(`/flags?client_id=${clientId}`),
        f(`/peer-docs?participant_id=${clientId}`)
      ]);
      setClientDetail({ user: userData, goals: Array.isArray(goalsData) ? goalsData : [], checkins: Array.isArray(checkinsData) ? checkinsData : [], flags: Array.isArray(flagsData) ? flagsData : [], docs: Array.isArray(notesData) ? notesData : [] });
    } catch { setClientDetail({ user: clientMap[clientId] || {}, goals: [], checkins: [], flags: [], docs: [] }); }
  };

  // ============ CLIENT DRILL-DOWN VIEW ============
  if (drilldownClient && clientDetail) {
    const cl = clientDetail.user || {};
    const recentCheckins = (clientDetail.checkins || []).slice(0, 14);
    const soberDays = recentCheckins.filter(c => c.stayed_sober).length;
    const avgMood = recentCheckins.length ? (recentCheckins.reduce((s, c) => s + (c.mood || 0), 0) / recentCheckins.length).toFixed(1) : 'N/A';
    const activeGoals = (clientDetail.goals || []).filter(g => g.status !== 'completed');
    const completedGoals = (clientDetail.goals || []).filter(g => g.status === 'completed');
    const unresolvedFlags = (clientDetail.flags || []).filter(fl => !fl.resolved);

    return (
      <div className="space-y-5" data-testid="client-drilldown">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setDrilldownClient(null); setClientDetail(null); }} className="rounded-full" data-testid="back-from-drilldown"><ArrowLeft size={16} /> Back to Dashboard</Button>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center text-xl font-bold text-teal-700">{cl.full_name?.charAt(0)}</div>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>{cl.full_name || 'Client'}</h2>
            <div className="text-sm text-muted-foreground">{cl.email} &middot; Status: <Badge className="bg-emerald-50 text-emerald-700 border-0 text-xs">{cl.status || 'active'}</Badge></div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-teal-700">{soberDays}</p>
            <p className="text-xs text-muted-foreground">Sober Days (14d)</p>
            {recentCheckins.length > 0 && (
              <div className="mt-2 text-left">
                {recentCheckins.filter(c => c.stayed_sober === false).length > 0 && (
                  <p className="text-[10px] text-red-600">Relapse days: {recentCheckins.filter(c => c.stayed_sober === false).length}</p>
                )}
                {cl.profile?.drug_of_choice && <p className="text-[10px] text-muted-foreground">DOC: {cl.profile.drug_of_choice}</p>}
                {cl.profile?.sobriety_date && <p className="text-[10px] text-muted-foreground">Sobriety: {cl.profile.sobriety_date}</p>}
              </div>
            )}
          </CardContent></Card>
          <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-sky-700">{avgMood}</p>
            <p className="text-xs text-muted-foreground">Avg Mood</p>
          </CardContent></Card>
          <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-emerald-700">{completedGoals.length}</p>
            <p className="text-xs text-muted-foreground">Goals Done</p>
          </CardContent></Card>
          <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-red-600">{unresolvedFlags.length}</p>
            <p className="text-xs text-muted-foreground">Active Flags</p>
          </CardContent></Card>
        </div>

        {/* Needs Attention */}
        {unresolvedFlags.length > 0 && (
          <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-red-400">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-red-700 flex items-center gap-2"><AlertTriangle size={14} /> Needs Attention</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {unresolvedFlags.map(fl => (
                <div key={fl.id} className="p-3 rounded-lg bg-red-50 text-sm">
                  <p className="font-medium text-red-800">{fl.type?.replace(/_/g, ' ') || 'Flag'}</p>
                  <p className="text-xs text-red-600 mt-1">{fl.reason || fl.notes || 'No details'}</p>
                  <p className="text-[10px] text-red-500 mt-1">Action: Review situation, check in with client, update care plan if needed</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Recent Check-ins */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity size={14} /> Recent Check-ins</CardTitle></CardHeader>
          <CardContent>
            {recentCheckins.length > 0 ? (
              <div className="space-y-1.5">
                {recentCheckins.map((c, i) => (
                  <div key={`item-${i}`} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-xs">
                    <span>{new Date(c.date || c.created_at).toLocaleDateString()}</span>
                    <div className="flex gap-2 items-center">
                      <span>Mood: <strong>{c.mood}/10</strong></span>
                      {c.stayed_sober !== undefined && <Badge className={c.stayed_sober ? 'bg-emerald-50 text-emerald-700 border-0 text-[10px]' : 'bg-red-50 text-red-700 border-0 text-[10px]'}>{c.stayed_sober ? 'Sober' : 'Relapse'}</Badge>}
                      {c.worked_today && <Badge className="bg-sky-50 text-sky-700 border-0 text-[10px]">Worked</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground text-center py-3">No recent check-ins</p>}
          </CardContent>
        </Card>

        {/* Active Goals */}
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target size={14} /> Goals ({activeGoals.length} active, {completedGoals.length} completed)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {(clientDetail.goals || []).map(g => (
                <div key={g.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-xs">
                  <div className="flex items-center gap-2">
                    {g.status === 'completed' ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Target size={14} className="text-amber-500" />}
                    <span className="font-medium">{g.title}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {g.progress !== undefined && <Progress value={g.progress} className="w-16 h-1.5" />}
                    <Badge variant="outline" className="text-[10px]">{g.status}</Badge>
                  </div>
                </div>
              ))}
              {(clientDetail.goals || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No goals set</p>}
            </div>
          </CardContent>
        </Card>

        {/* Peer Docs for this client */}
        {(clientDetail.docs || []).length > 0 && (
          <Card className="border-0 shadow-sm rounded-xl">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Mic size={14} /> Peer Documentation</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(clientDetail.docs || []).slice(0, 5).map(d => (
                <div key={d.id} className="p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center justify-between mb-1">
                    <Badge className="bg-teal-50 text-teal-700 text-[10px]">{d.interaction_type?.replace('_', ' ')}</Badge>
                    <span className="text-[10px] text-muted-foreground">{formatDate(d.created_at)}</span>
                  </div>
                  <p className="text-xs">{d.reframed_text || d.original_text}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ============ MAIN DASHBOARD VIEW ============
  return (
    <div className="space-y-6" data-testid="peer-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>Peer Support Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm">Welcome, {user.full_name}</p>
        </div>
        {!hasBio && (
          <Button onClick={() => navigate('/bio')} variant="outline" className="rounded-full gap-2 border-teal-300 text-teal-700" data-testid="create-profile-prompt">
            <User size={16} /> Create Your Profile
          </Button>
        )}
      </div>

      {/* Top Row Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="peer-availability-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-emerald-600" /> Emergency Availability</CardTitle></CardHeader>
          <CardContent className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{availability ? 'Marked available' : 'Marked unavailable'}</p>
              <p className="text-xs text-muted-foreground">Clients can contact you when you are available.</p>
            </div>
            <Switch checked={availability} onCheckedChange={handleAvailabilityToggle} disabled={availabilityLoading} data-testid="peer-availability-toggle" />
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setExpandedStat(expandedStat === 'pending' ? null : 'pending')} data-testid="peer-pending-approvals">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Bell className="w-4 h-4 text-amber-600" /> Pending Approvals <ChevronDown size={14} className={`ml-auto transition-transform ${expandedStat === 'pending' ? 'rotate-180' : ''}`} /></CardTitle></CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{totalPending}</p>
            <p className="text-xs text-muted-foreground">Goals + roadmap approvals waiting</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setExpandedStat(expandedStat === 'progress' ? null : 'progress')} data-testid="peer-client-progress">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-sky-600" /> Client Progress</CardTitle></CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{assignedClients.length}</p>
            <p className="text-xs text-muted-foreground">Active clients — click to drill down</p>
          </CardContent>
        </Card>
      </div>

      {/* Client Progress Drill-down */}
      {expandedStat === 'progress' && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-sky-400" data-testid="client-progress-drilldown">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Your Caseload — Full Breakdown</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setExpandedStat(null)}><X size={14} /></Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {assignedClients.map(c => {
              const clientCheckins = checkins.filter(ch => ch.user_id === c.id);
              const recentMood = clientCheckins.length > 0 ? clientCheckins[0]?.mood : null;
              return (
                <div key={c.id} className="p-3 rounded-xl bg-slate-50 hover:bg-teal-50 cursor-pointer transition-colors flex items-center justify-between" onClick={() => loadClientDetail(c.id)} data-testid={`caseload-client-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-sm font-bold text-teal-700">{c.full_name?.charAt(0)}</div>
                    <div>
                      <p className="text-sm font-medium">{c.full_name}</p>
                      <div className="flex gap-2 mt-0.5">
                        {c.profile?.demographics?.housing_status && <Badge variant="outline" className="text-[10px]">{c.profile.demographics.housing_status}</Badge>}
                        {c.profile?.demographics?.recovery_stage && <Badge variant="outline" className="text-[10px]">{c.profile.demographics.recovery_stage}</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {recentMood && <Badge className="bg-sky-50 text-sky-700 border-0 text-xs">Mood: {recentMood}/10</Badge>}
                    <ChevronRight size={14} className="text-teal-500" />
                  </div>
                </div>
              );
            })}
            {assignedClients.length === 0 && <p className="text-sm text-muted-foreground text-center py-3">No clients assigned yet</p>}
          </CardContent>
        </Card>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        {[
          { key: 'caseload', icon: Users, value: assignedClients.length, label: 'My Caseload', color: 'teal' },
          { key: 'peerdocs', icon: Mic, value: peerDocs.length, label: 'Peer Docs', color: 'sky' },
          { key: 'notes', icon: ClipboardList, value: notes.length, label: 'Session Notes', color: 'violet' },
          { key: 'appts', icon: CalendarDays, value: todayAppts.length, label: 'Upcoming Appts', color: 'amber' },
          { key: 'flags', icon: Flag, value: activeFlags.length, label: 'Active Flags', color: 'red' },
        ].map(s => (
          <Card key={s.key} className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setExpandedStat(expandedStat === s.key ? null : s.key)} data-testid={`stat-${s.key}`}>
            <CardContent className="p-4">
              <div className={`w-8 h-8 rounded-lg bg-${s.color}-50 flex items-center justify-center mb-2`}>
                <s.icon className={`w-4 h-4 text-${s.color}-600`} />
              </div>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">{s.label} <ChevronRight size={10} /></p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Stat Card Drill-downs */}
      {expandedStat === 'caseload' && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-teal-400" data-testid="drilldown-caseload">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">My Caseload</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setExpandedStat(null)}><X size={14} /></Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {assignedClients.map(c => (
              <div key={c.id} className="p-3 rounded-xl bg-slate-50 hover:bg-teal-50 cursor-pointer transition-colors flex items-center justify-between" onClick={() => loadClientDetail(c.id)} data-testid={`drill-client-${c.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-sm font-bold text-teal-700">{c.full_name?.charAt(0)}</div>
                  <div>
                    <p className="text-sm font-medium">{c.full_name}</p>
                    <p className="text-xs text-muted-foreground">{c.email}</p>
                  </div>
                </div>
                <ChevronRight size={14} className="text-teal-500" />
              </div>
            ))}
            {assignedClients.length === 0 && <p className="text-sm text-muted-foreground text-center py-3">No clients assigned</p>}
          </CardContent>
        </Card>
      )}

      {expandedStat === 'appts' && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-400" data-testid="drilldown-appts">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Upcoming Appointments</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setExpandedStat(null)}><X size={14} /></Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {todayAppts.map(a => (
              <div key={a.id} className="rounded-xl border overflow-hidden" data-testid={`appt-detail-${a.id}`}>
                <div className="p-3 bg-amber-50/50 cursor-pointer flex items-center justify-between" onClick={() => setExpandedAppt(expandedAppt === a.id ? null : a.id)}>
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium capitalize">{a.type} Session</p>
                      <p className="text-xs text-muted-foreground">{a.date} at {a.time}</p>
                    </div>
                  </div>
                  {expandedAppt === a.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
                {expandedAppt === a.id && (
                  <div className="p-3 bg-white border-t space-y-2 text-xs animate-in fade-in-0 duration-200">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Participant</span><span className="font-medium">{clientMap[a.participant_id]?.full_name || clientMap[a.client_id]?.full_name || 'N/A'}</span></div>
                      <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Type</span><span className="font-medium capitalize">{a.type}</span></div>
                      <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Status</span><Badge variant="outline" className="text-[10px]">{a.status}</Badge></div>
                      <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Location</span><span className="font-medium">{a.location || 'Not specified'}</span></div>
                    </div>
                    {a.notes && <div className="bg-muted/20 rounded-lg p-2 italic">{a.notes}</div>}
                  </div>
                )}
              </div>
            ))}
            {todayAppts.length === 0 && <p className="text-sm text-muted-foreground text-center py-3">No upcoming appointments</p>}
          </CardContent>
        </Card>
      )}

      {expandedStat === 'flags' && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-red-400" data-testid="drilldown-flags">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base text-red-700">Active Flags</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setExpandedStat(null)}><X size={14} /></Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {activeFlags.map(fl => (
              <div key={fl.id} className="p-3 rounded-lg bg-red-50 cursor-pointer hover:bg-red-100 transition-colors" onClick={() => { const cid = fl.client_id || fl.user_id; if (cid) loadClientDetail(cid); }}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-red-800">{clientMap[fl.client_id]?.full_name || 'Client'}: {fl.type?.replace(/_/g, ' ')}</p>
                  <ChevronRight size={14} className="text-red-400" />
                </div>
                <p className="text-xs text-red-600 mt-1">{fl.reason || fl.notes || 'No details'}</p>
                <p className="text-[10px] text-red-500 mt-1">Flagged: {formatDate(fl.created_at)}</p>
              </div>
            ))}
            {activeFlags.length === 0 && <p className="text-sm text-muted-foreground text-center py-3">No active flags</p>}
          </CardContent>
        </Card>
      )}

      {/* Pending Approvals - Full Inline Review */}
      {totalPending > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-400" data-testid="pending-approvals-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Bell className="w-4 h-4 text-amber-600" /> Pending Approvals ({totalPending})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {goalRequests.map(req => (
              <div key={req.id} className="rounded-xl border border-amber-200 overflow-hidden" data-testid={`goal-request-${req.id}`}>
                <div className="p-3 bg-amber-50 flex items-start justify-between gap-2 cursor-pointer" onClick={() => setExpandedApproval(expandedApproval === req.id ? null : req.id)}>
                  <div className="flex items-start gap-2">
                    <Target size={16} className="text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-900 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); loadClientDetail(req.user_id); }}>{req.user_name} requests goal progress</p>
                      <p className="text-xs text-amber-700 mt-0.5">Goal: <strong>{req.goal_title}</strong> &mdash; Requested: {req.requested_progress}%</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" className="h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1" onClick={(e) => { e.stopPropagation(); handleGoalReview(req.id, 'approve'); }} data-testid={`approve-goal-${req.id}`}><Check size={12} /> Approve</Button>
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-xs gap-1 border-amber-300" onClick={(e) => { e.stopPropagation(); setExpandedApproval(expandedApproval === req.id ? null : req.id); setReviewForm({ feedback: '', progress: req.requested_progress }); }} data-testid={`review-goal-${req.id}`}>
                      <Eye size={12} /> Review
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-xs gap-1 text-red-600 border-red-200" onClick={(e) => { e.stopPropagation(); handleGoalReview(req.id, 'reject'); }} data-testid={`reject-goal-${req.id}`}><X size={12} /> Reject</Button>
                  </div>
                </div>
                {expandedApproval === req.id && (
                  <div className="p-3 bg-white border-t border-amber-200 space-y-3 animate-in fade-in-0 duration-200" data-testid={`review-panel-${req.id}`}>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div className="bg-muted/30 rounded-lg p-2"><p className="font-semibold text-muted-foreground mb-1">Submitted</p><p>{new Date(req.created_at).toLocaleString()}</p></div>
                      <div className="bg-muted/30 rounded-lg p-2"><p className="font-semibold text-muted-foreground mb-1">Progress Change</p><p>{req.current_progress || 0}% → <strong>{req.requested_progress}%</strong></p></div>
                      <div className="bg-muted/30 rounded-lg p-2"><p className="font-semibold text-muted-foreground mb-1">Goal</p><p className="font-medium">{req.goal_title}</p></div>
                    </div>
                    {req.notes && <div className="bg-amber-50/50 rounded-lg p-3 text-sm border border-amber-100"><p className="text-xs font-semibold text-amber-600 mb-1">Client's Notes</p>"{req.notes}"</div>}
                    {req.evidence_url && <div className="bg-sky-50 rounded-lg p-2 text-xs border border-sky-100"><p className="font-semibold text-sky-600 mb-1">Evidence Submitted</p><a href={req.evidence_url} target="_blank" rel="noreferrer" className="text-sky-700 underline">View attachment</a></div>}
                    <div className="space-y-1.5">
                      <Label className="text-xs">Adjust Progress (%)</Label>
                      <Input type="number" min={0} max={100} value={reviewForm.progress} onChange={e => setReviewForm({ ...reviewForm, progress: parseInt(e.target.value) || 0 })} className="h-8 text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Feedback</Label>
                      <DictationInput value={reviewForm.feedback} onChange={(val) => setReviewForm({ ...reviewForm, feedback: val })} placeholder="Add notes or encouragement..." rows={2} showCleanup={false} data-testid="review-feedback-dictation" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-xs" onClick={() => handleGoalReview(req.id, 'approve')}>Approve</Button>
                      <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white rounded-full text-xs" onClick={() => handleGoalReview(req.id, 'modify')}>Modify</Button>
                      <Button size="sm" variant="outline" className="rounded-full text-xs text-red-600" onClick={() => handleGoalReview(req.id, 'reject')}>Send Back</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {roadmapApprovals.map(m => (
              <div key={m.id} className="rounded-xl border border-sky-200 overflow-hidden" data-testid={`roadmap-approval-${m.id}`}>
                <div className="p-3 bg-sky-50 flex items-start justify-between gap-2 cursor-pointer" onClick={() => setExpandedApproval(expandedApproval === m.id ? null : m.id)}>
                  <div className="flex items-start gap-2">
                    <Route size={16} className="text-sky-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-sky-900 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); loadClientDetail(m.user_id); }}>{m.user_name} completed a roadmap step</p>
                      <p className="text-xs text-sky-700 mt-0.5"><strong>{m.milestone_title}</strong></p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" className="h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1" onClick={(e) => { e.stopPropagation(); handleRoadmapReview(m.id, 'approve'); }} data-testid={`approve-roadmap-${m.id}`}><Check size={12} /> Approve</Button>
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-xs gap-1" onClick={(e) => { e.stopPropagation(); setExpandedApproval(expandedApproval === m.id ? null : m.id); }} data-testid={`review-roadmap-${m.id}`}>
                      <Eye size={12} /> Review
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-xs gap-1 text-red-600 border-red-200" onClick={(e) => { e.stopPropagation(); handleRoadmapReview(m.id, 'reject', 'Needs more work'); }} data-testid={`reject-roadmap-${m.id}`}><X size={12} /></Button>
                  </div>
                </div>
                {expandedApproval === m.id && (
                  <div className="p-3 bg-white border-t border-sky-200 space-y-3 animate-in fade-in-0 duration-200" data-testid={`review-panel-${m.id}`}>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-muted/30 rounded-lg p-2"><p className="font-semibold text-muted-foreground mb-1">Submitted</p><p>{new Date(m.submitted_at || m.created_at).toLocaleString()}</p></div>
                      <div className="bg-muted/30 rounded-lg p-2"><p className="font-semibold text-muted-foreground mb-1">Milestone</p><p className="font-medium">{m.milestone_title}</p></div>
                    </div>
                    {m.story && <div className="bg-sky-50/50 rounded-lg p-3 text-sm border border-sky-100"><p className="text-xs font-semibold text-sky-600 mb-1">Full Write-Up</p><p>{m.story}</p></div>}
                    {m.story_refined && <div className="bg-teal-50/50 rounded-lg p-3 text-sm border border-teal-100"><p className="text-xs font-semibold text-teal-600 mb-1">AI-Refined Version</p><p>{m.story_refined}</p></div>}
                    {m.evidence_url && <div className="bg-emerald-50 rounded-lg p-2 text-xs border border-emerald-100"><p className="font-semibold text-emerald-600 mb-1">Evidence</p><a href={m.evidence_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline">View attachment</a></div>}
                    <div className="grid grid-cols-2 gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-xs" onClick={() => handleRoadmapReview(m.id, 'approve')}>Approve</Button>
                      <Button size="sm" variant="outline" className="rounded-full text-xs text-red-600" onClick={() => handleRoadmapReview(m.id, 'reject', 'Needs more work')}>Reject</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Service Ledger + Court Cases */}
      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="service-ledger-section">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}><DollarSign className="w-4 h-4 text-emerald-600" /> Service Ledger</CardTitle>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-50 text-emerald-700 border-0 text-xs">{services.length} total</Badge>
              <Button variant="ghost" size="sm" className="text-teal-600 text-xs" onClick={() => navigate('/service-ledger')}>View all</Button>
            </div>
          </CardHeader>
          <CardContent>
            {services.length > 0 ? (
              <div className="space-y-2">
                {services.slice(0, 6).map(s => (
                  <div key={s.id} className="rounded-xl border overflow-hidden" data-testid={`service-card-${s.id}`}>
                    <div className="p-2.5 bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer flex items-center justify-between" onClick={() => setExpandedService(expandedService === s.id ? null : s.id)}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge className="text-[10px] shrink-0" variant={s.status === 'completed' ? 'default' : 'secondary'}>{s.status}</Badge>
                        <span className="text-sm font-medium truncate">{s.service_type?.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {s.cost && <span className="text-xs font-medium text-emerald-700">${s.cost}</span>}
                        {expandedService === s.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </div>
                    {expandedService === s.id && (
                      <div className="p-2.5 bg-white border-t space-y-2 text-xs animate-in fade-in-0 duration-200" data-testid={`service-detail-${s.id}`}>
                        <div className="grid grid-cols-2 gap-2">
                          <div><span className="text-muted-foreground">Participant:</span> <span className="font-medium cursor-pointer text-teal-700 hover:underline" onClick={() => { const cid = s.participant_id; if (cid) loadClientDetail(cid); }}>{clientMap[s.participant_id]?.full_name || s.participant_id?.slice(0, 8)}</span></div>
                          <div><span className="text-muted-foreground">Date:</span> {formatDate(s.service_date || s.created_at)}</div>
                          <div><span className="text-muted-foreground">Provider:</span> {s.provider || 'Staff'}</div>
                          <div><span className="text-muted-foreground">Duration:</span> {s.duration || 'N/A'}</div>
                          {s.start_time && <div><span className="text-muted-foreground">Start Time:</span> {s.start_time}</div>}
                        </div>
                        {s.notes && <p className="bg-muted/30 rounded-lg p-2 italic">{s.notes}</p>}
                        {s.brief_summary && <p className="bg-sky-50 rounded-lg p-2 border border-sky-100"><span className="font-semibold text-sky-600">Summary:</span> {s.brief_summary}</p>}
                        <Button size="sm" variant="outline" className="text-xs rounded-full gap-1" onClick={() => navigate('/service-ledger')}><Edit size={12} /> Edit in Ledger</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No service entries yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl" data-testid="court-cases-section">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}><Gavel className="w-4 h-4 text-indigo-600" /> Court Cases</CardTitle>
            <div className="flex items-center gap-2">
              <Badge className="bg-indigo-50 text-indigo-700 border-0 text-xs">{courtCases.length} total</Badge>
              <Button variant="ghost" size="sm" className="text-teal-600 text-xs" onClick={() => navigate('/court')}>View all</Button>
            </div>
          </CardHeader>
          <CardContent>
            {courtCases.length > 0 ? (
              <div className="space-y-2">
                {courtCases.slice(0, 6).map(c => (
                  <div key={c.id} className="rounded-xl border overflow-hidden" data-testid={`court-card-${c.id}`}>
                    <div className="p-2.5 bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer flex items-center justify-between" onClick={() => setExpandedCase(expandedCase === c.id ? null : c.id)}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge className="text-[10px] shrink-0" variant={c.status === 'active' ? 'destructive' : 'secondary'}>{c.status}</Badge>
                        <span className="text-sm font-medium truncate">{c.case_number || c.case_type || 'Case'}</span>
                      </div>
                      {expandedCase === c.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                    {expandedCase === c.id && (
                      <div className="p-2.5 bg-white border-t space-y-2 text-xs animate-in fade-in-0 duration-200" data-testid={`court-detail-${c.id}`}>
                        <div className="grid grid-cols-2 gap-2">
                          <div><span className="text-muted-foreground">Participant:</span> <span className="font-medium cursor-pointer text-teal-700 hover:underline" onClick={() => { const cid = c.participant_id; if (cid) loadClientDetail(cid); }}>{clientMap[c.participant_id]?.full_name || c.participant_name || 'N/A'}</span></div>
                          <div><span className="text-muted-foreground">Type:</span> {c.case_type?.replace(/_/g, ' ')}</div>
                          <div><span className="text-muted-foreground">Court:</span> {c.court_name || 'N/A'}</div>
                          <div><span className="text-muted-foreground">Judge:</span> {c.judge || 'N/A'}</div>
                          <div><span className="text-muted-foreground">PO:</span> {c.probation_officer || 'N/A'}</div>
                          <div><span className="text-muted-foreground">Next Date:</span> {c.next_hearing_date ? formatDate(c.next_hearing_date) : 'TBD'}</div>
                        </div>
                        {c.charges && <div className="bg-red-50 rounded-lg p-2 border border-red-100"><span className="font-semibold text-red-600">Charges:</span> {c.charges}</div>}
                        {c.conditions && <div className="bg-amber-50 rounded-lg p-2 border border-amber-100"><span className="font-semibold text-amber-600">Conditions:</span> {c.conditions}</div>}
                        {c.notes && <div className="bg-muted/30 rounded-lg p-2 italic">{c.notes}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No court cases tracked yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Caseload + Appointments */}
      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>My Caseload</CardTitle></CardHeader>
          <CardContent>
            {assignedClients.length > 0 ? (
              <div className="space-y-2">
                {assignedClients.map(c => (
                  <div key={c.id} className="p-3 rounded-xl bg-muted/30 hover:bg-teal-50 transition-colors cursor-pointer" onClick={() => loadClientDetail(c.id)} data-testid={`caseload-item-${c.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-sm font-semibold text-teal-700">{c.full_name?.charAt(0)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.full_name}</p>
                        <div className="flex gap-1 flex-wrap mt-0.5">
                          {c.profile?.demographics?.housing_status && <Badge variant="outline" className="text-[10px]">{c.profile.demographics.housing_status}</Badge>}
                          {c.profile?.demographics?.recovery_stage && <Badge variant="outline" className="text-[10px]">{c.profile.demographics.recovery_stage}</Badge>}
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-teal-500" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground mb-2">No clients assigned to you yet.</p>
                {clients.length > 0 && (
                  <div className="bg-sky-50 rounded-xl p-3 border border-sky-100 cursor-pointer hover:bg-sky-100 transition-colors" onClick={() => navigate('/clients')}>
                    <p className="text-xs text-sky-700 font-medium">People Needing Support: {clients.length}</p>
                    <p className="text-xs text-sky-600 mt-0.5">Contact your coordinator for caseload assignments.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Upcoming Appointments</CardTitle></CardHeader>
          <CardContent>
            {todayAppts.length > 0 ? (
              <div className="space-y-2">
                {todayAppts.slice(0, 5).map(a => (
                  <div key={a.id} className="rounded-xl bg-muted/30 overflow-hidden cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setExpandedAppt(expandedAppt === `main-${a.id}` ? null : `main-${a.id}`)}>
                    <div className="flex items-center gap-3 p-2.5">
                      <CalendarDays className="w-5 h-5 text-teal-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium capitalize">{a.type} Session</p>
                        <p className="text-xs text-muted-foreground">{a.date} at {a.time} {a.location ? `— ${a.location}` : ''}</p>
                      </div>
                      <ChevronRight size={14} className="text-muted-foreground" />
                    </div>
                    {expandedAppt === `main-${a.id}` && (
                      <div className="px-3 pb-3 border-t border-slate-100 space-y-1.5 text-xs animate-in fade-in-0 duration-200">
                        <div className="grid grid-cols-2 gap-2 pt-2">
                          <div><span className="text-muted-foreground">Participant:</span> {clientMap[a.participant_id]?.full_name || clientMap[a.client_id]?.full_name || 'N/A'}</div>
                          <div><span className="text-muted-foreground">Status:</span> {a.status}</div>
                        </div>
                        {a.notes && <p className="bg-muted/30 rounded-lg p-2 italic">{a.notes}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No upcoming appointments.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Housing Section with Our Square logos */}
      <Card className="border-0 shadow-sm rounded-2xl" data-testid="housing-section">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <img src="/images/our-square-logo.png" alt="Our Square" className="w-5 h-5 object-contain" />
            Our Square Transitional Housing
          </CardTitle>
          <Button variant="ghost" size="sm" className="text-teal-600 text-xs" onClick={() => navigate('/housing')}>Manage</Button>
        </CardHeader>
        <CardContent>
          {houses.length > 0 ? (
            <div className="space-y-2">
              {houses.map(h => {
                const isComingSoon = h.name?.includes('4408') || h.name?.includes('4407') || h.address?.includes('4408') || h.address?.includes('4407');
                const available = (h.total_beds || 0) - (h.occupied_beds || 0);
                const occupancyPct = h.total_beds ? Math.round((h.occupied_beds || 0) / h.total_beds * 100) : 0;
                return (
                  <div key={h.id} className={`rounded-xl border overflow-hidden ${isComingSoon ? 'opacity-75' : ''}`} data-testid={`house-card-${h.id}`}>
                    <div className="p-3 bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer flex items-center justify-between" onClick={() => !isComingSoon && setExpandedHouse(expandedHouse === h.id ? null : h.id)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-white border flex items-center justify-center shrink-0">
                          <img src="/images/our-square-logo.png" alt="Our Square" className="w-6 h-6 object-contain" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold truncate">{h.name}</p>
                            {isComingSoon && <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">Coming Soon</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} /> {h.address}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {isComingSoon ? (
                          <Badge className="bg-amber-50 text-amber-700 border-0"><Bed size={11} className="mr-1" /> {h.total_beds || h.capacity} beds planned</Badge>
                        ) : (
                          <Badge className={available > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            <Bed size={11} className="mr-1" /> {available} / {h.total_beds} available
                          </Badge>
                        )}
                        {!isComingSoon && (expandedHouse === h.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </div>
                    </div>
                    {expandedHouse === h.id && !isComingSoon && (
                      <div className="p-3 bg-white border-t space-y-2 animate-in fade-in-0 duration-200" data-testid={`house-detail-${h.id}`}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Type</span><span className="font-medium capitalize">{h.housing_type?.replace(/_/g, ' ') || h.program_type?.replace(/_/g, ' ') || 'Transitional'}</span></div>
                          <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Gender</span><span className="font-medium capitalize">{h.gender_designation || 'Mixed'}</span></div>
                          <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Status</span><Badge variant="outline" className="text-[10px] capitalize">{h.intake_status || 'open'}</Badge></div>
                          <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Occupancy</span><span className="font-medium">{occupancyPct}%</span></div>
                        </div>
                        <Progress value={occupancyPct} className="h-2" />
                        {h.description && <p className="text-xs text-muted-foreground">{h.description}</p>}
                        <Button size="sm" variant="link" className="text-xs p-0 h-auto" onClick={() => navigate('/housing')}>View full property details <ChevronRight size={12} /></Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No housing properties found.</p>
          )}
        </CardContent>
      </Card>

      {/* Peer Docs - Full Scrollable List */}
      <Card className="border-0 shadow-sm rounded-2xl" data-testid="peer-docs-section">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Peer Documentation</CardTitle>
          <Button variant="ghost" size="sm" className="text-teal-600 text-xs" onClick={() => navigate('/peer-docs')}>Create New</Button>
        </CardHeader>
        <CardContent>
          {peerDocs.length > 0 ? (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {peerDocs.map(d => (
                <div key={d.id} className="rounded-xl border overflow-hidden" data-testid={`peerdoc-${d.id}`}>
                  <div className="p-3 bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer flex items-center justify-between" onClick={() => setExpandedDoc(expandedDoc === d.id ? null : d.id)}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge className="bg-teal-50 text-teal-700 text-[10px] shrink-0">{d.interaction_type?.replace('_', ' ')}</Badge>
                      <span className="text-sm truncate">{d.reframed_text?.substring(0, 60) || d.original_text?.substring(0, 60)}...</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-[10px]">{d.approval_status || 'pending'}</Badge>
                      <span className="text-[10px] text-muted-foreground">{formatDate(d.created_at)}</span>
                      {expandedDoc === d.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </div>
                  {expandedDoc === d.id && (
                    <div className="p-3 bg-white border-t space-y-2 text-xs animate-in fade-in-0 duration-200" data-testid={`peerdoc-detail-${d.id}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <div><span className="text-muted-foreground">Participant:</span> <span className="font-medium cursor-pointer text-teal-700 hover:underline" onClick={() => { if (d.participant_id) loadClientDetail(d.participant_id); }}>{clientMap[d.participant_id]?.full_name || 'N/A'}</span></div>
                        <div><span className="text-muted-foreground">Created:</span> {new Date(d.created_at).toLocaleString()}</div>
                      </div>
                      {d.reframed_text && (
                        <div className="bg-teal-50 rounded-lg p-3 border border-teal-100">
                          <p className="text-xs font-semibold text-teal-600 mb-1">Refined Documentation</p>
                          <p className="text-sm">{d.reframed_text}</p>
                        </div>
                      )}
                      {d.original_text && d.original_text !== d.reframed_text && (
                        <div className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs font-semibold text-muted-foreground mb-1">Original Text</p>
                          <p className="text-sm">{d.original_text}</p>
                        </div>
                      )}
                      {d.analysis && (
                        <div className="bg-sky-50 rounded-lg p-2 border border-sky-100">
                          <p className="text-xs font-semibold text-sky-600 mb-1">Analysis</p>
                          {typeof d.analysis === 'object' ? (
                            <div className="space-y-1">
                              {d.analysis.core_issues && <p><span className="text-muted-foreground">Issues:</span> {d.analysis.core_issues}</p>}
                              {d.analysis.strengths && <p><span className="text-muted-foreground">Strengths:</span> {d.analysis.strengths}</p>}
                              {d.analysis.needs && <p><span className="text-muted-foreground">Needs:</span> {d.analysis.needs}</p>}
                            </div>
                          ) : <p>{d.analysis}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No peer documentation yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Weekly Performance Scorecard */}
      {scorecard?.metrics && (
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="performance-scorecard">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <TrendingUp className="w-4 h-4 text-teal-600" /> Weekly Performance Scorecard
            </CardTitle>
            <Badge className="bg-teal-50 text-teal-700 border-0 text-xs">Last 7 days</Badge>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: 'Services Logged', val: scorecard.metrics.services_logged?.current || 0, trend: scorecard.metrics.services_logged?.trend, color: 'teal', icon: Briefcase },
                { label: 'Service Hours', val: scorecard.metrics.service_hours?.current || 0, unit: 'hrs', color: 'sky', icon: Clock },
                { label: 'Clients Seen', val: `${scorecard.metrics.clients_seen?.current || 0}/${scorecard.metrics.clients_seen?.total_assigned || 0}`, color: 'violet', icon: Users },
                { label: 'Approvals', val: scorecard.metrics.approvals_handled?.current || 0, color: 'amber', icon: Check },
                { label: 'Peer Docs', val: scorecard.metrics.peer_docs?.current || 0, trend: scorecard.metrics.peer_docs?.trend, color: 'emerald', icon: FileText },
              ].map((m, i) => (
                <div key={`item-${i}`} className="bg-muted/30 rounded-xl p-3 text-center">
                  <m.icon size={16} className={`mx-auto mb-1.5 text-${m.color}-600`} />
                  <p className="text-xl font-bold">{m.val}{m.unit ? ` ${m.unit}` : ''}</p>
                  <p className="text-[10px] text-muted-foreground">{m.label}</p>
                  {m.trend && m.trend !== '' && m.trend !== 'flat' && (
                    <span className={`text-[10px] font-medium ${m.trend.startsWith('+') || m.trend === 'new' ? 'text-emerald-600' : 'text-red-500'}`}>
                      {m.trend === 'new' ? 'New!' : `${m.trend} vs last wk`}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-3">
              {[
                { label: 'Flags Created', val: scorecard.metrics.flags_created?.current || 0, color: 'red' },
                { label: 'Flags Resolved', val: scorecard.metrics.flags_resolved?.current || 0, color: 'emerald' },
                { label: 'Group Notes', val: scorecard.metrics.group_notes?.current || 0, color: 'violet' },
                { label: 'Session Notes', val: scorecard.metrics.session_notes?.current || 0, color: 'sky' },
                { label: 'Appointments', val: scorecard.metrics.appointments_conducted?.current || 0, color: 'amber' },
              ].map((m, i) => (
                <div key={`item-${i}`} className="bg-muted/20 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold">{m.val}</p>
                  <p className="text-[10px] text-muted-foreground">{m.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Weekly Digest - Client Summaries */}
      {weeklyDigest?.clients && weeklyDigest.clients.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl" data-testid="weekly-digest-section">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <CalendarDays className="w-4 h-4 text-sky-600" /> Weekly Digest
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge className="bg-red-50 text-red-700 border-0 text-xs">{weeklyDigest.summary?.clients_needing_attention || 0} need attention</Badge>
              <Badge className="bg-sky-50 text-sky-700 border-0 text-xs">{weeklyDigest.summary?.total_pending_approvals || 0} pending</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {weeklyDigest.clients.map(c => (
              <div key={c.id} className="rounded-xl border overflow-hidden" data-testid={`digest-client-${c.id}`}>
                <div className={`p-3 cursor-pointer hover:bg-muted/30 transition-colors flex items-center justify-between ${c.needs_attention ? 'bg-red-50/30 border-l-4 border-l-red-300' : 'bg-muted/10'}`}
                  onClick={() => setExpandedDigestClient(expandedDigestClient === c.id ? null : c.id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-sm font-bold text-teal-700">{c.name?.charAt(0)}</div>
                    <div>
                      <p className="text-sm font-medium cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); loadClientDetail(c.id); }}>{c.name}</p>
                      <div className="flex gap-1.5 items-center mt-0.5">
                        <Badge className="bg-sky-50 text-sky-700 border-0 text-[10px]">Mood: {c.avg_mood}/10 {c.mood_trend === 'improving' ? ' +' : c.mood_trend === 'declining' ? ' -' : ''}</Badge>
                        <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">Sober: {c.sober_days}/{c.checkins}d</Badge>
                        {c.pending_approvals > 0 && <Badge className="bg-amber-50 text-amber-700 border-0 text-[10px]">{c.pending_approvals} pending</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.needs_attention && <AlertTriangle size={14} className="text-red-500" />}
                    {expandedDigestClient === c.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>
                {expandedDigestClient === c.id && (
                  <div className="p-3 bg-white border-t space-y-2 animate-in fade-in-0 duration-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="bg-muted/30 rounded-lg p-2 cursor-pointer hover:bg-sky-50 transition-colors" onClick={() => loadClientDetail(c.id)}>
                        <span className="text-muted-foreground block">Check-ins</span>
                        <span className="font-medium">{c.checkins} of 7 days</span>
                        {c.missed_days > 0 && <span className="text-red-500 block text-[10px]">{c.missed_days} missed</span>}
                      </div>
                      <div className="bg-muted/30 rounded-lg p-2 cursor-pointer hover:bg-emerald-50 transition-colors" onClick={() => loadClientDetail(c.id)}>
                        <span className="text-muted-foreground block">Sober Days</span>
                        <span className="font-medium">{c.sober_days} days</span>
                        {c.drug_of_choice && <span className="text-orange-600 block text-[10px] font-medium mt-0.5">DOC: {c.drug_of_choice}</span>}
                        {c.sobriety_date && <span className="text-muted-foreground block text-[10px]">Since: {c.sobriety_date}</span>}
                      </div>
                      <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Work Days</span><span className="font-medium">{c.work_days}</span></div>
                      <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Last Check-in</span><span className="font-medium">{c.last_checkin}</span></div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-muted/20 rounded-lg p-2 text-center"><span className="font-bold text-lg">{c.active_goals}</span><span className="text-muted-foreground block text-[10px]">Active Goals</span></div>
                      <div className="bg-muted/20 rounded-lg p-2 text-center"><span className="font-bold text-lg text-emerald-600">{c.completed_goals}</span><span className="text-muted-foreground block text-[10px]">Completed</span></div>
                      <div className="bg-muted/20 rounded-lg p-2 text-center"><span className="font-bold text-lg text-amber-600">{c.new_achievements}</span><span className="text-muted-foreground block text-[10px]">Achievements</span></div>
                    </div>
                    {/* Needs Attention Details */}
                    {c.alerts && c.alerts.length > 0 && (
                      <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                        <p className="text-xs font-semibold text-red-700 mb-1.5 flex items-center gap-1"><AlertTriangle size={12} /> Needs Attention</p>
                        <div className="space-y-1.5">
                          {c.alerts.map((alert, ai) => (
                            <div key={ai} className="flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-red-800">{alert}</p>
                                <p className="text-[10px] text-red-600">
                                  {alert.includes('check-in') && 'Follow up with client to ensure they are completing daily check-ins.'}
                                  {alert.includes('mood') && 'Schedule a one-on-one to discuss emotional well-being and adjust care plan.'}
                                  {alert.includes('declining') && 'Mood trend warrants immediate attention — consider intervention or counseling referral.'}
                                  {alert.includes('appointment') && 'Contact client about missed appointments and reschedule.'}
                                  {alert.includes('No check-in') && 'Client has not checked in — prioritize outreach immediately.'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <Button size="sm" variant="link" className="text-xs p-0 h-auto text-teal-700" onClick={() => loadClientDetail(c.id)}>View full client profile <ChevronRight size={12} /></Button>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Quick Actions</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-teal-50" onClick={() => navigate('/peer-docs')} data-testid="quick-peer-doc">
            <Mic size={18} className="text-teal-600" /><span className="text-xs">New Peer Doc</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-sky-50" onClick={() => navigate('/messages')} data-testid="quick-peer-messages">
            <MessageSquare size={18} className="text-sky-600" /><span className="text-xs">Messages</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-amber-50" onClick={() => navigate('/flags')}>
            <AlertTriangle size={18} className="text-amber-600" /><span className="text-xs">Flag Client</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-violet-50" onClick={() => navigate('/session-notes')}>
            <ClipboardList size={18} className="text-violet-600" /><span className="text-xs">Session Note</span>
          </Button>
        </CardContent>
      </Card>

      <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 text-center">
        <p className="text-sm text-teal-800 font-medium">Communication Policy</p>
        <p className="text-xs text-teal-600 mt-1">All communications with court/probation officers are routed through the Executive Director. This protects both you and the people you support.</p>
      </div>
    </div>
  );
}
